import { useMutation } from "@tanstack/react-query";
import camelcase from "camelcase-keys";
import truncate from "lodash/truncate";

import { NotificationsLevel } from "../../lib/enums";
import {
  InteractiveParams,
  RlcsParams,
  SourcesState
} from "../../modules/samples/components/reanalyze/reanalyze-form";
import { show } from "../../notifications/notifications";
import { ErrorResponse } from "../../requests/@types";
import api from "../../services/api";
import { route } from "../../shim/jsbridge";
import { AnalysisSelections } from "../@types/fileanalysisform";
import {
  filterNonLocalAnalyses,
  getDynamicAnalysisList,
  getObjectValue
} from "./utils";

type FileAnalysisFormState = {
  files: Array<File>;
  fileUrl: string;
};

type Payload = {
  fileForm: FileAnalysisFormState;
  analyses: AnalysisSelections;
  rlcsParams: RlcsParams;
  interactiveParams: InteractiveParams;
  sources?: SourcesState;
};

const useUploadFromUrl = () => {
  const { mutateAsync, isPending, error, reset } = useMutation({
    mutationFn: (payload: Payload) => {
      const { analyses, fileForm, interactiveParams, rlcsParams, sources } =
        payload;
      const { localOnly } = camelcase(analyses);
      const newAnalyses = localOnly
        ? filterNonLocalAnalyses(analyses)
        : analyses;

      const isRlcs = Boolean(newAnalyses.rl_dynamic_analysis);
      const isInteractive = Boolean(
        newAnalyses.rl_dynamic_analysis_interactive
      );

      const uploadToCloud = newAnalyses.ti_cloud;
      const rlcsPlatform = getObjectValue(rlcsParams, "platform", isRlcs);
      const rlcsGeolocation = getObjectValue(rlcsParams, "geolocation", isRlcs);
      const rlcsLocale = getObjectValue(rlcsParams, "locale", isRlcs);
      const rlcsInternetSimulation = getObjectValue(
        rlcsParams,
        "internet_simulation",
        isRlcs
      );
      const rlcsSampleName = getObjectValue(rlcsParams, "sample_name", isRlcs);
      const rlcsExecutionTime = getObjectValue(
        rlcsParams,
        "execution_timeout",
        isRlcs
      );
      const interactivePlatform = getObjectValue(
        interactiveParams,
        "platform",
        isInteractive
      );
      const interactiveTimeout = getObjectValue(
        interactiveParams,
        "execution_timeout",
        isInteractive
      );
      const interactiveSampleName = getObjectValue(
        interactiveParams,
        "sample_name",
        isInteractive
      );
      const interactiveGeolocation = getObjectValue(
        interactiveParams,
        "geolocation",
        isInteractive
      );
      const interactiveLocale = getObjectValue(
        interactiveParams,
        "locale",
        isInteractive
      );

      const fileOriginLink = fileForm.fileUrl;

      return api.uploadFromUrl(
        fileForm.fileUrl,
        uploadToCloud,
        rlcsPlatform,
        rlcsGeolocation,
        rlcsLocale,
        rlcsExecutionTime,
        rlcsInternetSimulation,
        rlcsSampleName,
        interactivePlatform,
        interactiveTimeout,
        interactiveSampleName,
        interactiveGeolocation,
        interactiveLocale,
        getDynamicAnalysisList(analyses),
        localOnly,
        sources?.sourceTag,
        fileOriginLink
      );
    },
    onSuccess: (data, variables) => {
      const {
        data: { uploadIds, warnings = {} }
      } = data ?? {};

      const { fileForm, analyses } = variables;

      show({
        level: NotificationsLevel.SUCCESS,
        message: `Successfully started URL upload ${truncate(fileForm.fileUrl, {
          length: 32
        })} `
      });

      const isRlInteractiveAnalysisSelected =
        analyses.rl_dynamic_analysis_interactive;

      if (Object.keys(warnings).length) {
        Object.values(warnings).forEach((warning: { message: string }) => {
          show({
            level: NotificationsLevel.WARNING,
            sticky: true,
            message: warning.message
          });
        });
      } else if (isRlInteractiveAnalysisSelected && !analyses.local_only) {
        uploadIds?.forEach((uploadId: string) => {
          window.open(
            route("rl_interactive_analysis_for_upload", [uploadId]),
            "_blank"
          );
        });
      }
    },
    onError: (_, variables) => {
      const { fileForm } = variables;
      show({
        level: NotificationsLevel.ERROR,
        message: `${truncate(fileForm.fileUrl, {
          length: 32
        })} upload failed.`
      });
    }
  });

  return {
    mutate: mutateAsync,
    isPending,
    reset,
    error: error as ErrorResponse<Record<string, string[]>> | null
  };
};

export default useUploadFromUrl;
